import React, { useState, useEffect, Suspense, lazy } from 'react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import './styles/App.css';
import { supabase } from './supabaseClient';

// Componentes críticos (carga inmediata)
import Login from './components/Login';
import VideoIntro from './components/VideoIntro';
import MainLayout from './components/MainLayout';
import LoadingSpinner from './components/LoadingSpinner';
import GameLoadingFallback from './components/GameLoadingFallback';
import StatusBar from './components/StatusBar';

// Hooks personalizados
import { useServiceWorker } from './hooks/useServiceWorker';

// Componentes con lazy loading
const Home = lazy(() => import(/* webpackChunkName: "home" */ './components/HomeComponent'));
const Story = lazy(() => import(/* webpackChunkName: "story" */ './components/Story'));
const EventDetails = lazy(() => import(/* webpackChunkName: "event" */ './components/EventDetails'));
const Accommodation = lazy(() => import(/* webpackChunkName: "accommodation" */ './components/Accommodation'));
const RSVP = lazy(() => import(/* webpackChunkName: "rsvp" */ './components/RSVP'));
const Trivial = lazy(() => import(/* webpackChunkName: "trivial" */ './components/Trivial'));
const FAQ = lazy(() => import(/* webpackChunkName: "faq" */ './components/FAQ'));
const Admin = lazy(() => import(/* webpackChunkName: "admin" */ './components/Admin'));
const Game = lazy(() => import(/* webpackChunkName: "game" */ './components/Game'));
const GameMobile = lazy(() => import(/* webpackChunkName: "game-mobile" */ './components/GameMobile'));
const Blog = lazy(() => import(/* webpackChunkName: "blog" */ './components/Blog'));
const Photobooth = lazy(() => import(/* webpackChunkName: "photobooth" */ './components/Photobooth'));
const DotsMemories = lazy(() => import(/* webpackChunkName: "dots-memories" */ './components/DotsMemories'));

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasSeenIntro, setHasSeenIntro] = useState(false); // Siempre mostrar video // Cambiado de vuelta a false para mostrar video

  // Detectar si es dispositivo móvil
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      
      // Detección mejorada - SEPARAR PC de MÓVIL claramente
      const isMobileWidth = screenWidth <= 768; // Solo anchos muy pequeños son móviles
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      const hasTouchScreen = 'ontouchstart' in window || (navigator.maxTouchPoints > 0 && screenWidth <= 768);
      const isSmallScreen = Math.min(screenWidth, screenHeight) <= 600; // Muy pequeño
      
      // Considerar móvil SOLO si es claramente un dispositivo móvil
      const detectedAsMobile = (isMobileDevice && screenWidth <= 1024) || isMobileWidth || (hasTouchScreen && isSmallScreen);
      
      // Debug info (solo en desarrollo)
      if (process.env.NODE_ENV === 'development') {
        console.log('Mobile detection:', { // eslint-disable-line no-console
          screenWidth,
          screenHeight,
          userAgent: userAgent.substring(0, 100) + '...',
          isMobileWidth,
          isMobileDevice,
          hasTouchScreen,
          isSmallScreen,
          detectedAsMobile
        });
      }
      
      setIsMobile(detectedAsMobile);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Service Worker management
  const { isUpdateAvailable, isOffline, registration } = useServiceWorker({
    onUpdate: () => {
      console.log('Service Worker actualizado'); // eslint-disable-line no-console
    },
    onOffline: () => {
      console.log('Aplicación offline'); // eslint-disable-line no-console
    },
    onOnline: () => {
      console.log('Aplicación online'); // eslint-disable-line no-console
    },
  });

  const updateServiceWorker = () => {
    if (registration && registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  };

  // Verificar autenticación al cargar
  useEffect(() => {
    const checkAuth = () => {
      const storedAuth = localStorage.getItem('wedding-auth');

      if (storedAuth === 'authenticated') {
        setIsAuthenticated(true);
      }

      // Siempre mostrar el video introductorio
      setHasSeenIntro(false);

      setIsLoading(false);
    };

    // Simular tiempo de carga
    setTimeout(checkAuth, 1000);
  }, []);

  const handleLogin = async (username, password) => {
    // Normalizar valores para evitar errores de espacios o mayúsculas
    const cleanUser = username.trim().toLowerCase();
    const cleanPass = password.trim();

    const { data } = await supabase
      .from('users')
      .select('*')
      .ilike('users', cleanUser)
      .eq('password', cleanPass);

    // Solo mostrar logs en desarrollo y sin datos sensibles
    if (
      process.env.REACT_APP_DEBUG === 'true' &&
      process.env.NODE_ENV === 'development'
    ) {
      console.log('LOGIN ATTEMPT:', { // eslint-disable-line no-console
        username: cleanUser,
        success: !!(data && data.length > 0),
      });
    }

    if (data && data.length > 0) {
      setIsAuthenticated(true);
      localStorage.setItem('wedding-auth', 'authenticated');
      localStorage.setItem('wedding-user', cleanUser); // Guardar nombre para uso en Blog
      return { success: true };
    }
    return { success: false, message: 'Credenciales incorrectas' };
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('wedding-auth');
    localStorage.removeItem('wedding-user');
  };

  const handleVideoEnd = () => {
    setHasSeenIntro(true);
    // No guardar en localStorage para que se muestre siempre
  };

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <h2>💒 Dani & Ana</h2>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Mostrar video introductorio si no se ha visto
    if (!hasSeenIntro) {
      return <VideoIntro onVideoEnd={handleVideoEnd} />;
    }

    // Mostrar login después del video
    return <Login onLogin={handleLogin} />;
  }

  return (
    <>
      <StatusBar
        isOffline={isOffline}
        isUpdateAvailable={isUpdateAvailable}
        onUpdate={updateServiceWorker}
      />
      <Router>
        <MainLayout onLogout={handleLogout}>
          <Suspense fallback={<LoadingSpinner message="Cargando página..." />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/home" element={<Home />} />
              <Route path="/story" element={<Story />} />
              <Route path="/details" element={<EventDetails />} />
              <Route path="/accommodation" element={<Accommodation />} />
              <Route path="/rsvp" element={<RSVP />} />
              {/* <Route path="/guestlist" element={<GuestList />} /> */}
              <Route path="/faq" element={<FAQ />} />
              <Route path="/trivial" element={<Trivial />} />
              <Route
                path="/game"
                element={
                  <Suspense fallback={<GameLoadingFallback />}>
                    {isMobile ? <GameMobile /> : <Game />}
                  </Suspense>
                }
              />
              <Route path="/blog" element={<Blog />} />
              <Route path="/photobooth" element={<Photobooth />} />
              <Route path="/dots-memories" element={<DotsMemories />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <SpeedInsights />
          <Analytics />
        </MainLayout>
      </Router>
    </>
  );
}

export default App;
